import { Capacitor } from '@capacitor/core';

/**
 * Field-client declaration (server side: backend/src/common/middleware/client-mode.ts).
 *
 * The server-side idle timeout applies to the web console only (architecture
 * §8). The server cannot yet tell a responder's phone from a console browser,
 * so the app says so on its requests (all but field ingress, see below):
 * `X-Client-Mode: field` when it runs in
 * the native shell (Capacitor) or on the field-mode routes (/field/*), the same
 * routes that already skip the console's client-side idle timeout (FieldLayout
 * does not mount useIdleTimeout).
 *
 * Without it, a phone idle in a pocket past the tenant's session timeout got a
 * 401 SESSION_TIMEOUT on its first read after resuming, and the 401 handler in
 * services/api.ts discarded the token the SOS button sends.
 *
 * The declaration exempts nothing else: the IP allowlist, licence and RBAC
 * still apply to every request that carries it.
 */

export const CLIENT_MODE_HEADER = 'X-Client-Mode';
export const FIELD_CLIENT_MODE = 'field';

/** Field-mode routes: `/field` and everything under it, nothing that merely starts with "field". */
export function isFieldPath(pathname: string): boolean {
  return pathname === '/field' || pathname.startsWith('/field/');
}

function isNativeShell(): boolean {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
}

/** True in the native shell, or while the web app is on a field-mode route. */
export function isFieldClient(): boolean {
  if (isNativeShell()) return true;
  return typeof window !== 'undefined' && isFieldPath(window.location.pathname);
}

/** Headers to merge into an API request: the declaration, or nothing. */
export function clientModeHeaders(): Record<string, string> {
  return isFieldClient() ? { [CLIENT_MODE_HEADER]: FIELD_CLIENT_MODE } : {};
}

/**
 * The life-safety writes the server exempts BY ROUTE (backend field-ingress.ts):
 * SOS, positions, check-ins and tracking-session start. The declaration means
 * nothing to them, so it is left off on purpose. The web app calls the API
 * cross-origin, and a request header the API's CORS policy does not allow
 * makes the browser block the whole request. An SOS must never depend on that
 * (a backend rolled back to a build without the header, a CORS edit).
 */
const FIELD_INGRESS_CALLS: ReadonlySet<string> = new Set([
  'POST /personnel-tracking/sos',
  'POST /personnel-tracking/positions',
  'POST /personnel-tracking/check-ins',
  'POST /personnel-tracking/sessions/start',
]);

export function isFieldIngressCall(method: string | undefined, url: string | undefined): boolean {
  if (!method || !url) return false;
  const path = url
    .split('?')[0]
    .replace(/^[a-z]+:\/\/[^/]+/i, '') // absolute URL: drop the origin
    .replace(/^\/api(?=\/)/, '') // and the API prefix
    .replace(/\/+$/, '');
  return FIELD_INGRESS_CALLS.has(`${method.toUpperCase()} ${path}`);
}

/** The server's 401 for a console session idle past the tenant's timeout. */
export function isSessionTimeoutResponse(status: number | undefined, data: unknown): boolean {
  if (status !== 401 || !data || typeof data !== 'object') return false;
  const code = (data as { error?: { code?: unknown } }).error?.code;
  return code === 'SESSION_TIMEOUT';
}
